import { useCallback, useState } from "react";
import {
  UserListResponse,
  UserHistoryResponse,
  UserData,
  UserHistoryData,
} from "../../types/dashboard";
import { api } from "../../utils/api";
import toast from "react-hot-toast";

interface UserDashboardState {
  // 사용자 목록/검색 데이터
  userList: UserData[];
  userListTotalCount: number;

  // 사용자 히스토리 데이터
  userHistory: UserHistoryData[];
  userHistoryTotalCount: number;

  // 현재 선택된 사용자
  selectedUserId: string | null;
}

export const useUserDashboard = () => {
  const [state, setState] = useState<UserDashboardState>({
    userList: [],
    userListTotalCount: 0,
    userHistory: [],
    userHistoryTotalCount: 0,
    selectedUserId: null,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleError = (error: any) => {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      "알 수 없는 오류가 발생했습니다.";
    setError(errorMessage);
  };

  // 사용자 목록 조회 (15개씩)
  const getUserDataList = useCallback(async (page: number = 1) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get<UserListResponse>(
        `/admin/v1/user/list?page=${page}`
      );

      if (response.data.status) {
        setState((prev) => ({
          ...prev,
          userList: response.data.data,
          userListTotalCount: response.data.total_cnt,
        }));
      } else {
        throw new Error("API 응답 상태가 실패입니다.");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 사용자 검색 (15개씩)
  const getUserSearch = useCallback(async (search: string, page: number = 1) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get<UserListResponse>(
        `/admin/v1/user/search?page=${page}&q=${encodeURIComponent(search)}`
      );

      if (response.data.status) {
        setState((prev) => ({
          ...prev,
          userList: response.data.data,
          userListTotalCount: response.data.total_cnt,
        }));
      } else {
        throw new Error("검색 결과를 가져올 수 없습니다.");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 사용자 히스토리 조회 (10개씩)
  const getUserHistory = useCallback(async (userId: string, page: number = 1) => {
    setIsHistoryLoading(true);
    setError(null);

    try {
      const response = await api.get<UserHistoryResponse>(
        `/admin/v1/user/history?page=${page}&u_id=${userId}`
      );

      if (response.data.status) {
        setState((prev) => ({
          ...prev,
          userHistory: response.data.data,
          userHistoryTotalCount: response.data.total_cnt,
          selectedUserId: userId,
        }));
      } else {
        throw new Error("사용자 히스토리를 가져올 수 없습니다.");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsHistoryLoading(false);
    }
  }, []);

  const createUser = useCallback(async (id: string, password: string, nickname: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.post(
        `/admin/v1/master/create_expert_acoount`,
        {
          id: id,
          password: password,
          nickname: nickname,
        },
      );
      if (response.data.status) {
        toast.success("사용자 생성에 성공했습니다.");
        return true;
      } else {
        toast.error("사용자 생성에 실패했습니다.");
        return false;
      }
    } catch (error) {
      handleError(error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const userRankUpdate = useCallback(async (id: string, sig_typ: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post(`/admin/v1/user/rank/update`, {
        id: id,
        sig_typ: sig_typ,
      });

      if (response.data.status) {
        return true;
      } else {
        throw new Error("권한 변경에 실패했습니다.");
      }
    } catch (error) {
      handleError(error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateUserRankInState = useCallback((userId: string, newRank: string) => {
    setState((prev) => ({
      ...prev,
      userList: prev.userList.map((user) =>
        user.id === userId ? { ...user, sig_typ: newRank } : user
      ),
    }));
  }, []);

  const clearSelectedUser = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedUserId: null,
      userHistory: [],
      userHistoryTotalCount: 0,
    }));
  }, []);

  // 상태 초기화
  const resetState = () => {
    setState({
      userList: [],
      userListTotalCount: 0,
      userHistory: [],
      userHistoryTotalCount: 0,
      selectedUserId: null,
    });
    setError(null);
  };

  return {
    userList: state.userList,
    userHistory: state.userHistory,
    selectedUserId: state.selectedUserId,

    isLoading,
    isHistoryLoading,
    error,

    getUserDataList,
    getUserSearch,
    getUserHistory,
    userRankUpdate,
    updateUserRankInState,
    clearSelectedUser,
    createUser,
    resetState,

    totalUserCount: state.userListTotalCount,
    totalHistoryCount: state.userHistoryTotalCount,
    isEmpty: state.userList.length === 0,
  };
};
